import React from 'react';
import {
  inject,
  observer,
} from 'mobx-react';
import {
  ErrorMessage,
  Field,
  FieldArray,
  Form,
  Formik,
} from 'formik';
import * as yup from 'yup';
import {
  Row,
  Col,
  ControlLabel,
  FormGroup,
  FormControl,
  Alert,
  Button,
  InputGroup,
} from 'react-bootstrap';
import LoadingButton from '../../../components/LoadingButton/LoadingButton';
import PageLoading from '../../../components/PageLoading/PageLoading';
import {
  Portlet,
  PortletBody,
  PortletCaption,
  PortletTitle,
} from '../../../components/Bootstrap/Portlet';
import { withTranslation } from 'react-i18next';
import ManualAddStore from '../../../stores/manual-add-store';
import MaterialStore from '../../../stores/material-store';
import NotifierStore from '../../../stores/notifier-store';
import MixStore from '../../../stores/mix-store';
import type {
  IMaterial,
  IMaterialWithDetails,
} from '../../../../types/material/MaterialModels';

interface ComponentProps {
  manualAddStore: ManualAddStore;
  materialStore: MaterialStore;
  notifierStore: NotifierStore;
  mixStore: MixStore;
  onCancel: () => void;
  t: (key: string, args?: any) => string;
}

interface ComponentState {
  error: string | null;
}

class ManualAddForm extends React.Component<ComponentProps, ComponentState> {
  constructor(props: any) {
    super(props);
    this.state = {
      error: null,
    };
  }

  initialValues() {
    return {
      id: null,
      extraProductItemId: null,
      extraProductItemCode: '',
      extraProductItemDescription: '',
      unitSymbol: '',
      constituentItems: [],
    };
  };

  renderMixDesign(values: any, setValues: any) {
    const { materialStore, t } = this.props;
    const { otherMaterials, weighedMaterials, meteredMaterials, materialDetails } = materialStore;
    const filteredMaterials = [ ...otherMaterials, ...weighedMaterials, ...meteredMaterials ]
      .filter((v: IMaterial) => !values.constituentItems.find((x: any) => x.constituentItemId === v.itemId));

    const onConstituentChange = (e: any, index: number, values: any, setValues: any) => {
      const constituentItemId = Number(e.target.value);
      const material: IMaterial = filteredMaterials.find((v: IMaterial) => v.itemId === constituentItemId);
      // @ts-ignore remove when materialStore is typed
      const md: IMaterialWithDetails = materialDetails[material.materialDetailId];
      let payload = { ...values };

      if (md?.itemBatchUnitSymbol) {
        payload.constituentItems[index] = {
          ...values.constituentItems[index],
          constituentItemId,
          constituentItemCode: material?.code,
          unitSymbol: md?.itemBatchUnitSymbol,
          unitId: md?.itemBatchUnitId,
        };
      } else {
        payload.constituentItems[index] = {
          ...values.constituentItems[index],
          constituentItemId,
          constituentItemCode: material?.code,
          unitSymbol: material?.itemInventoryUnitSymbol,
          unitId: material?.itemInventoryUnitId,
        };
      }

      setValues(payload);
    };

    return (
      <React.Fragment>
        <ControlLabel>{''}</ControlLabel>
        <FieldArray
          name="constituentItems"
          render={arrayHelpers => (
            <div className={'mix-design-form'}>
              {values.constituentItems && values.constituentItems.length > 0 ? (
                values.constituentItems.map((item: any, index: number) => (

                  <Row key={index}>
                    <Col sm={3} xs={5}>
                      <FormGroup>
                        <ControlLabel>{t('Constituent')}</ControlLabel>
                        <FormControl
                          name={`constituentItems.${index}.constituentItemId`}
                          componentClass="select"
                          value={values.constituentItems[index].constituentItemId || ''}
                          onChange={e => onConstituentChange(e, index, values, setValues)}
                          bsSize="sm"
                        >
                          {filteredMaterials.map((option: any) => (
                            <option key={option.itemId} value={option.itemId}>
                              {option.code} {option.itemShortDescription}
                            </option>
                          ))}
                          <option value={values.constituentItems[index].constituentItemId || ''}>
                            {values.constituentItems[index].constituentItemCode} {values.constituentItems[index].constituentItemShortDescription}
                          </option>
                        </FormControl>
                        <ErrorMessage component="span" name={`constituentItems.${index}.constituentItemId`} className="help-block" />
                      </FormGroup>
                    </Col>
                    <Col sm={3} xs={5}>
                      <FormGroup>
                        <ControlLabel>{t('Qty')}</ControlLabel>
                        <InputGroup>
                          <Field name={`constituentItems.${index}.quantity`}
                            placeholder="0"
                            type="number"
                            value={values.constituentItems[index].quantity || ''}
                            className="form-control input-sm" />
                          <InputGroup.Addon>
                            {item.unitSymbol}
                          </InputGroup.Addon>
                        </InputGroup>
                        <ErrorMessage component="span" name={`constituentItems.${index}.quantity`} className="help-block" />
                      </FormGroup>
                    </Col>
                    <Col sm={1} xs={1}>
                      <a className="btn btn-sm btn-default btn-action"
                        title="Remove"
                        onClick={() => arrayHelpers.remove(index)}>
                        <i className="fa fa-remove"/>
                      </a>
                    </Col>
                  </Row>

                ))
              ) : '' }

              <Row>
                <Col sm={3}>
                  <a className="btn btn-sm btn-default btn-add"
                    title="Add mix design" onClick={() => arrayHelpers.push({
                      constituentItemId: '',
                      quantity: 0,
                    })}>
                    <i className="fa fa-plus"/>
                  </a>
                </Col>
              </Row>
            </div>
          )}
        />
      </React.Fragment>
    );
  }

  async onSubmit(values: any, actions: any) {
    const  { manualAddStore, mixStore, notifierStore, t } = this.props;

    this.setState({
      error: null,
    });

    let promise;
    if (values.id) {
      promise = manualAddStore.updateManualAdd(values);
    } else {
      promise = manualAddStore.saveManualAdd(values);
    }

    // @ts-ignore
    try {
      await promise;
      await manualAddStore.loadManualAddList();

      notifierStore.showSuccess('Saved');
      mixStore.selectAdjustedProduct(null);
      actions.resetForm({
        values: this.initialValues(),
      });
      actions.setSubmitting(false);
    } catch (e: any) {
      this.setState({
        error: t('Couldn\'t save', { error: e?.message }),
      });
      actions.setValues(values);
      actions.setSubmitting(false);
    }
  }

  render() {
    const  { manualAddStore, mixStore, onCancel, t } = this.props;
    const { allExtra } = mixStore;
    const { manualAddList, selectedManualAddId } = manualAddStore;

    const handleItemSelect = (e: any, values: any, setValues: any) => {
      const extraProductItemId = e.target.value;
      const extra = allExtra.find((v: any) => v.itemId === Number(extraProductItemId));

      setValues({
        ...values,
        extraProductItemId,
        extraProductItemCode: extra ? extra.itemCode : '',
        extraProductItemDescription: extra ? extra.itemDescription : '',
        unitSymbol: extra ? extra.unitSymbol : '',
        unitId: extra ? extra.unitId : '',
      });
    };

    const getManualAdd = () => {
      const manualAddItem = manualAddStore.getManualAddById(manualAddStore.selectedManualAddId);
      return {
        ...this.initialValues(),
        ...manualAddItem,
      };
    };

    const getExtraProductsFiltered = () => {
      return selectedManualAddId
        ? allExtra
        : allExtra.filter((v: any) => !manualAddList.find((x: any) => x.extraProductItemId === v.itemId));
    };

    return (
      <Portlet>
        <PortletTitle>
          <PortletCaption>
            {t('Manual Add')} {selectedManualAddId ? getManualAdd().extraProductItemCode : ''}
          </PortletCaption>
        </PortletTitle>
        <PortletBody>
          <div>
            <Formik
              enableReinitialize
              initialValues={
                !selectedManualAddId
                  ? this.initialValues()
                  : getManualAdd()
              }
              validationSchema={yup.object().shape({
                extraProductItemCode: yup.string().required('Required!'),
                constituentItems: yup.array().of(
                  yup.object().shape({
                    constituentItemId: yup.string().required('Required!'),
                  }),
                ),
              })}
              onSubmit={(values, actions) => this.onSubmit(values, actions)}
            >{({ values,
                setValues,
                errors,
                touched,
                isSubmitting,
              }) => (
                <Form className={'mix-form'}>
                  {isSubmitting &&
                    <PageLoading/>
                  }
                  <Row>
                    <Col sm={3} xs={6}>
                      <FormGroup className={errors.extraProductItemId && touched.extraProductItemId? 'has-error' : ''}>
                        <ControlLabel>{t('Extra product that adjust')}</ControlLabel>
                        <FormControl
                          name="extraProductItemId"
                          componentClass="select"
                          disabled={!!values.extraProductItemId}
                          value={values.extraProductItemId || ''}
                          onChange={e => handleItemSelect(e, values, setValues)}
                          bsSize="sm"
                        >
                          <option value=""/>
                          {getExtraProductsFiltered().map((option: any) => (
                            <option key={option.itemId} value={option.itemId}>
                              {option.itemCode} {option.itemDescription}
                            </option>
                          ))}
                        </FormControl>
                        <ErrorMessage component="span" name="extraProductItemId" className="help-block" />
                      </FormGroup>
                    </Col>
                    <Col sm={3} xs={6}>
                      <FormGroup>
                        <ControlLabel>{t('Code')}</ControlLabel>
                        <Field name="extraProductItemCode"
                          placeholder="Code"
                          disabled={true}
                          value={values.extraProductItemCode || ''}
                          className="form-control input-sm" />
                      </FormGroup>
                    </Col>
                    <Col sm={3} xs={6}>
                      <FormGroup>
                        <ControlLabel>{t('Description')}</ControlLabel>
                        <Field name="extraProductItemDescription"
                          placeholder="Description"
                          disabled={true}
                          value={values.extraProductItemDescription || ''}
                          className="form-control input-sm" />
                      </FormGroup>
                    </Col>
                    <Col sm={3} xs={6}>
                      <FormGroup>
                        <ControlLabel>{t('Unit')}</ControlLabel>
                        <Field name="unitSymbol"
                          disabled={true}
                          value={values.unitSymbol || ''}
                          className="form-control input-sm" />
                      </FormGroup>
                    </Col>
                  </Row>

                  {this.renderMixDesign(values, setValues)}

                  <Row>
                    <Col sm={12} className="text-right">
                      {!!this.state.error && <Alert bsStyle="danger">{this.state.error}</Alert>}
                      <Button onClick={onCancel}>{t('Cancel')}</Button>
                      {' '}
                      <LoadingButton
                        bsStyle="primary"
                        type="submit"
                        label={t('Save')}
                        loadingLabel={t('Save')}
                        isLoading={isSubmitting}
                        disabled={isSubmitting}
                      />
                    </Col>
                  </Row>
                </Form>
              )}
            </Formik>
          </div>
        </PortletBody>
      </Portlet>
    );
  }
}

export default withTranslation()(inject('mixStore', 'materialStore', 'notifierStore', 'manualAddStore')(observer(ManualAddForm)));
